/**
 * Collection Workflow
 *
 * Handles collection-specific workflow data fetching
 * Extracted from workflow-data.service.ts
 */

import {
  apiGet,
  isSuccessResponse,
  extractErrorMessage
} from '@/lib/utils/api-helpers';
import { transformInstitution } from '../transformers';
import { getLawFirmData } from '../services/law-firm.service';
import { getSignatories } from '../services/signatory.service';
import {
  extractDocuments,
  extractInstitutions,
  extractInventory
} from './base.workflow';
import type {
  WorkflowData,
  InstitutionForm,
  BackendCorrespondence
} from '../workflow-types';
import { getUserDisplayName } from '@/lib/utils/permissions';

/**
 * Fetch data needed for collection workflow
 * Similar to notification but for collection-ready institutions
 */
export async function getCollectionWorkflowData(
  matterId: number
): Promise<WorkflowData> {
  try {
    console.log('Fetching collection workflow data for matter:', matterId);

    // Fetch all data in parallel
    const [
      matterResponse,
      institutionsResponse,
      documentsResponse,
      inventoryResponse,
      correspondencesResponse
    ] = await Promise.all([
      apiGet(`/api/matters/${matterId}`),
      apiGet(
        `/api/proxy/matters/${matterId}/institutions?stage=collection&correspondenceType=collection-request`
      ),
      apiGet(`/api/proxy/matters/${matterId}/files`),
      apiGet(`/api/proxy/matters/${matterId}/inventory-items`),
      apiGet(`/api/proxy/matters/${matterId}/correspondences`)
    ]);

    console.log('Collection API responses:', {
      matter: isSuccessResponse(matterResponse),
      institutions: isSuccessResponse(institutionsResponse),
      documents: isSuccessResponse(documentsResponse),
      inventory: isSuccessResponse(inventoryResponse),
      correspondences: isSuccessResponse(correspondencesResponse)
    });

    // Check for matter data (critical)
    if (!isSuccessResponse(matterResponse)) {
      throw new Error(extractErrorMessage(matterResponse));
    }

    const matter = matterResponse.data as {
      id: number;
      deceasedName?: string;
      dateOfDeath?: string;
      caseReference?: string;
      lawFirmInternalReference?: string;
      actingSolicitor?: {
        id: number;
        firstName: string | null;
        lastName: string | null;
        email: string;
        hasSignature?: boolean;
      } | null;
    };

    // Extract data using base functions
    const institutions = extractInstitutions(institutionsResponse);
    const documents = extractDocuments(documentsResponse, 'Collection');
    const inventory = extractInventory(inventoryResponse);

    // Extract correspondences
    let correspondences: BackendCorrespondence[] = [];
    if (isSuccessResponse(correspondencesResponse)) {
      const data = correspondencesResponse.data as
        | BackendCorrespondence[]
        | {
            correspondences?: BackendCorrespondence[];
            json?: { correspondences?: BackendCorrespondence[] };
          };
      if (Array.isArray(data)) {
        correspondences = data;
      } else if (data?.json?.correspondences) {
        correspondences = data.json.correspondences;
      } else if (data?.correspondences) {
        correspondences = data.correspondences;
      }
    }

    // Filter institutions that already have collection drafts
    const draftInstitutionIds = new Set(
      correspondences
        .filter((c) => c.type === 'collection-request' && c.status === 'draft')
        .map((c) => (c.institution as { id?: number } | null)?.id)
    );

    // Get law firm data and signatories in parallel
    const [lawFirm, signatories] = await Promise.all([
      getLawFirmData(),
      getSignatories()
    ]);

    // Fetch institution forms for each institution
    const institutionsWithForms = await Promise.all(
      (institutions as Array<{ id?: number; [k: string]: unknown }>).map(
        async (inst) => {
          try {
            const formsResponse = await apiGet(
              `/api/proxy/matters/${matterId}/institutions/${inst.id}/forms`
            );

            let forms: InstitutionForm[] = [];
            if (isSuccessResponse(formsResponse)) {
              forms = formsResponse.data as InstitutionForm[];
            }

            return { institution: inst, forms };
          } catch (error) {
            console.warn(
              `Failed to fetch forms for institution ${inst.id}:`,
              error
            );
            return { institution: inst, forms: [] };
          }
        }
      )
    );

    // Transform institutions for workflow
    const workflowInstitutions = institutionsWithForms.map(
      ({ institution, forms }) =>
        transformInstitution(institution, inventory, forms)
    );

    // Filter out institutions with existing drafts
    const availableInstitutions = workflowInstitutions.filter(
      (inst) => !draftInstitutionIds.has(inst.id)
    );

    console.log('Collection workflow data summary:', {
      matterName: matter.deceasedName,
      totalInstitutions: institutions.length,
      availableInstitutions: availableInstitutions.length,
      draftsExist: draftInstitutionIds.size,
      documents: documents.length
    });

    return {
      matter: {
        id: matter.id,
        deceasedName: matter.deceasedName || '',
        dateOfDeath: matter.dateOfDeath || '',
        referenceNumber: matter.caseReference || '',
        lawFirmInternalReference: matter.lawFirmInternalReference || '',
        actingSolicitor: matter.actingSolicitor
          ? {
              id: matter.actingSolicitor.id,
              name: getUserDisplayName(matter.actingSolicitor),
              email: matter.actingSolicitor.email,
              hasSignature: matter.actingSolicitor.hasSignature || false
            }
          : null
      },
      institutions: availableInstitutions,
      availableDocuments: documents,
      availableSignatories: signatories,
      correspondences: correspondences, // Include backend correspondences with validation
      lawFirm
    };
  } catch (error) {
    console.error('Failed to fetch collection workflow data:', error);
    throw error;
  }
}
